import dayjs from "dayjs";
import Decimal from "decimal.js";
import $ from "jquery";
import Snap from "snapsvg-cjs-ts";
import { MapView } from "./Basemap";
import { CanvasTiles } from "./CanvasTiles";
import {
    ForecastTime,
    InitialTime,
    ShadeSelector,
    ShadeVariable,
} from "./Controller";
import { SHADE_PMTILES_CUTOFF } from "./DataAvailability";
import { Checkbox, Selectbox } from "./Form";
import { getMapMeta } from "./MapMeta";
import { readChoice, readFlag, readNumber } from "./UrlState";
import { dataPath } from "./paths";

type Level = 0 | 1 | 2 | 3;
type Elements = [string, string, string];
const getPreloadDebounceMs = (search: string) => {
    const requested = Number(
        new URLSearchParams(search).get("pmdebounce") ?? "100",
    );
    return requested === 50 || requested === 250 ? requested : 100;
};
const elementsMap = new Map<ShadeVariable, Elements>([
    ["GSMR", ["GSMR", "", ""]], ["GSMRG", ["GSMRG", "", ""]], ["GSMC", ["", "GSMC", ""]], ["GSMN", ["", "", ""]], ["GSMRC", ["GSMR", "GSMC", ""]], ["GSMSj", ["GSMSj", "", ""]], ["GSMSh", ["GSMSh", "", ""]], ["GSMSv", ["GSMSv", "", ""]], ["GSMV", ["GSMV", "", ""]],
    ["GS5R", ["GS5R", "", ""]], ["GS5Sj", ["GS5Sj", "", ""]], ["GS5Sh", ["GS5Sh", "", ""]], ["GS5N", ["", "", ""]],
    ["GSAR", ["GSAR", "", ""]], ["GSASj", ["GSASj", "", ""]], ["GSASh", ["GSASh", "", ""]], ["GSAN", ["", "", ""]],
    ["GSLR", ["GSLR", "", ""]], ["GSLN", ["", "", ""]],
    ["MSGL", ["MSGL", "", ""]],
    ["MSMR", ["MSMR", "", ""]], ["MSMRG", ["MSMRG", "", ""]], ["MSMN", ["", "", ""]], ["MSMRC", ["MSMR", "MSMC", ""]], ["MSMT", ["MSMT", "", ""]], ["MSMSj", ["MSMSj", "", ""]], ["MSMSh", ["MSMSh", "", ""]], ["MSMSv", ["MSMSv", "", ""]], ["MSMV", ["MSMV", "", ""]],
    ["LFMR", ["LFMR", "", ""]], ["LFMRC", ["LFMR", "LFMC", ""]], ["LFMN", ["", "", ""]], ["LFMT", ["LFMT", "", ""]], ["LFMSj", ["LFMSj", "", ""]], ["LFMSh", ["LFMSh", "", ""]],
    ["CWMH", ["", "", "CWMH"]],
    ["QMAR", ["QMAR", "", ""]], ["QMAN", ["", "", ""]], ["QMASj", ["QMASj", "", ""]], ["QMASh", ["QMASh", "", ""]],
]); // biome-ignore format:

export class Shade {
    private readonly shadeImage: ShadeImage;
    private readonly canvasTiles = new CanvasTiles();
    private readonly qualitySelector = $("#select_imgQ");
    private readonly clipCoast = new Checkbox("#cb_clipCoast");
    private changeCallback: (task: Promise<unknown>) => void = () => {};
    private level: Level = 0;
    private tilesActive = false;
    private initialized = false;
    private previousFt: Decimal | null = null;
    private preloadTimer: number | null = null;
    private readonly preloadDebounceMs = getPreloadDebounceMs(
        globalThis.location?.search ?? "",
    );
    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly mapView: MapView,
    ) {
        this.qualitySelector.on("change", () =>
            this.changeCallback(this.updateExtent()),
        );
        this.clipCoast.onChange(() => this.updateClipCoast());
        // 先読みが1時刻ぶん進むたびにタイムバーの配色 (time_unready) を更新
        this.canvasTiles.onPrefetchProgress(() => this.updateTabReadiness());

        this.shadeImage = new ShadeImage(
            this.shadeSelector,
            this.initialTime,
            forecastTime,
            this.clipCoast,
        );
        this.loadParams();
        // constructor中は非同期描画を始めない。MapViewとqualityから表示levelだけを
        // 確定し、App.refresh()のonForecastTimeChanged()で最初のcomposeを1回行う。
        const quality = this.qualitySelector.val() as "H" | "M" | "L";
        this.level = this.getLevel(quality);
        void this.shadeImage.setLevel(this.level);
    }
    loadParams() {
        this.clipCoast.value = readFlag(this.params, "sty_clipCoast", "0");
        this.updateClipCoast();
    }
    saveParams() {
        this.params.set("sty_clipCoast", this.clipCoast.value);
    }
    onChange(callback: (task: Promise<unknown>) => void) {
        this.changeCallback = callback;
    }
    onShadeVariableChanged() {
        return this.updateVarIni();
    }
    onInitialTimeChanged() {
        return this.updateVarIni();
    }
    onForecastTimeChanged() {
        if (!this.initialized || this.shouldUseTiles() !== this.tilesActive) {
            return this.updateVarIni();
        }
        return Promise.all([this.shadeImage.update(), this.updateCanvas()]);
    }
    getDisplayLevel() {
        return this.level;
    }
    prefetchCurrent() {
        const { shadeVariable, model } = this.shadeSelector.getParams();
        const elements = elementsMap.get(shadeVariable) as Elements;
        if (!this.canvasTiles.isSupported(elements) || !this.shouldUseTiles()) {
            return Promise.resolve();
        }
        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        return this.canvasTiles.prefetchCurrent(
            ini,
            ft,
            elements,
            this.level,
            this.getTileRange(this.level),
            model,
        );
    }
    async toCaptureMode() {
        if (this.tilesActive) {
            await this.updateCanvas();
            return;
        }
        const level = this.getLevel("H");
        await this.shadeImage.toCaptureMode(level);
    }
    async toDisplayMode() {
        if (this.tilesActive) {
            return;
        }
        const quality = this.qualitySelector.val() as "H" | "M" | "L";
        const level = this.getLevel(quality);
        await this.shadeImage.toDisplayMode(level);
    }
    private updateVarIni() {
        this.initialized = true;
        const { shadeVariable } = this.shadeSelector.getParams();
        const elements = elementsMap.get(shadeVariable) as Elements;
        this.tilesActive =
            this.canvasTiles.isSupported(elements) && this.shouldUseTiles();
        const imageUpdate = this.shadeImage.updateVarIni(!this.tilesActive);
        this.canvasTiles.setActive(this.tilesActive);
        return Promise.all([imageUpdate, this.updateCanvas()]);
    }
    private shouldUseTiles() {
        const { shadeVariable } = this.shadeSelector.getParams();
        const model = shadeVariable.slice(0, 3);
        const { ini } = this.initialTime.getParams();
        const cutoff =
            SHADE_PMTILES_CUTOFF[model] ?? SHADE_PMTILES_CUTOFF.default;
        if (model !== "QMA") {
            return ini.format("YYYYMMDD-HH") >= cutoff;
        }
        const { ft } = this.forecastTime.getParams();
        return ini.add(ft.toNumber(), "hour").format("YYYYMMDD-HH") >= cutoff;
    }
    updateExtent() {
        const quality = this.qualitySelector.val() as "H" | "M" | "L";
        const level = this.getLevel(quality);
        this.level = level;
        const imageUpdate = this.shadeImage.setLevel(level);
        return Promise.all([imageUpdate, this.updateCanvas()]);
    }
    private updateClipCoast() {
        this.shadeImage.updateClipCoast();
        this.canvasTiles.applyClip(this.clipCoast.value === "1");
    }
    private getLevel(quality: "H" | "M" | "L") {
        const scale =
            quality === "H"
                ? this.mapView.getScreenScale()
                : this.mapView.getDisplayScale();
        const { model } = this.shadeSelector.getParams();
        const unit = getMapMeta(model).kmPerPx;
        let level: Level;
        if (scale >= unit * 8) {
            level = 0;
        } else if (scale >= unit * 4) {
            level = 1;
        } else if (scale >= unit * 2) {
            level = 2;
        } else {
            level = 3;
        }
        if (quality === "L" && level > 1) {
            level = 1;
        }
        return level;
    }

    private updateCanvas() {
        if (!this.tilesActive) {
            return Promise.resolve();
        }
        const { shadeVariable, model } = this.shadeSelector.getParams();
        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        const range = this.getTileRange(this.level);
        return this.canvasTiles
            .compose(
                ini,
                ft,
                elementsMap.get(shadeVariable) as Elements,
                this.level,
                range,
                this.clipCoast.value === "1",
                model,
            )
            .then((composed) => {
                if (composed) {
                    this.preloadCanvas(ini, ft, range);
                }
                this.updateTabReadiness();
            });
    }

    // タイムバー全コマの先読み状況をタブ配色へ反映する (2026-08-02 発注者指示)。
    // 「表示中の要素のタイルが全て取得済み (bytesキャッシュ命中含む)」を完了とする。
    // 旧形式 (tilesActive=false) では触らない = 従来色のまま
    private updateTabReadiness() {
        if (!this.tilesActive) {
            return;
        }
        const { shadeVariable, model } = this.shadeSelector.getParams();
        const elements = elementsMap.get(shadeVariable) as Elements;
        const { ini } = this.initialTime.getParams();
        const range = this.getTileRange(this.level);
        const { interval, minimum, maximum } =
            this.forecastTime.getRangeParams();
        const ready = new Map<string, boolean>();
        for (
            let ft = new Decimal(minimum);
            ft.lte(maximum);
            ft = ft.add(interval)
        ) {
            ready.set(
                ft.toString(),
                this.canvasTiles.ftReady(
                    ini,
                    ft,
                    elements,
                    this.level,
                    range,
                    model,
                ),
            );
        }
        this.forecastTime.setTabReadiness(ready);
    }

    private preloadCanvas(
        initial: dayjs.Dayjs,
        ft: Decimal,
        range: { top: number; bottom: number; left: number; right: number },
    ) {
        const direction: 1 | -1 =
            this.previousFt === null || ft.gte(this.previousFt) ? 1 : -1;
        this.previousFt = ft;
        // 100ms デバウンス: 連打で通過したFTの先読みを積まず、最後の状態だけを
        // 起動する。検証・切戻し用にpmdebounce=250/50も受け付ける。
        if (this.preloadTimer !== null) {
            window.clearTimeout(this.preloadTimer);
        }
        this.preloadTimer = window.setTimeout(
            () => this.firePreload(initial, ft, range, direction),
            this.preloadDebounceMs,
        );
    }

    private firePreload(
        initial: dayjs.Dayjs,
        ft: Decimal,
        range: { top: number; bottom: number; left: number; right: number },
        direction: 1 | -1,
    ) {
        this.preloadTimer = null;
        // 前方12・後方6 (進行方向基準)。全FT先読みは要素・倍率の変更が多い
        // 利用実態に対して過剰なため廃止 (2026-08-02 発注者指示)。一度取得した
        // タイルは bytes キャッシュ (圧縮 LRU 200MB) に残るため、再訪は速いまま
        const forecastTimes = this.forecastTime.getNeighborFts(ft, direction);
        const { shadeVariable, model } = this.shadeSelector.getParams();
        this.canvasTiles.preload(
            initial,
            forecastTimes,
            elementsMap.get(shadeVariable) as Elements,
            this.level,
            range,
            model,
        );
    }

    private getTileRange(level: Level) {
        const { model } = this.shadeSelector.getParams();
        const meta = getMapMeta(model);
        const cropNum = meta.cropNum[level];
        if (level === 0) {
            return { top: 0, bottom: cropNum, left: 0, right: cropNum };
        }
        const { x, y, width, height, deg } = this.mapView.getViewBox();
        const tileWidth = meta.width / cropNum;
        const tileHeight = meta.height / cropNum;
        const clamp = (value: number) => Math.max(0, Math.min(cropNum, value));
        let left = clamp(Math.floor((x - meta.x0) / tileWidth));
        let top = clamp(Math.floor((y - meta.y0) / tileHeight));
        const columns = Number.isInteger(width / tileWidth)
            ? width / tileWidth + 1
            : Math.floor(width / tileWidth) + 2;
        const rows = Number.isInteger(height / tileHeight)
            ? height / tileHeight + 1
            : Math.floor(height / tileHeight) + 2;
        let right = clamp(left + columns);
        let bottom = clamp(top + rows);
        if (deg !== 0) {
            left = Math.max(0, left - 1);
            top = Math.max(0, top - 1);
            right = Math.min(cropNum, right + 1);
            bottom = Math.min(cropNum, bottom + 1);
        }
        return { top, bottom, left, right };
    }
}

class ShadeImage {
    private readonly loaded = new Map<
        string,
        Promise<(HTMLImageElement | null)[]>
    >();
    private readonly images: [Snap.Paper, Snap.Paper, Snap.Paper] = [
        Snap("#region_image_0"),
        Snap("#region_image_1"),
        Snap("#region_image_2"),
    ];
    private level: Level = 0;
    private isActive = false;
    private _isActive = false;
    constructor(
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly clipCoast: Checkbox,
    ) {}
    updateVarIni(isActive: boolean) {
        for (const image of this.images) {
            image.attr({ display: isActive ? "block" : "none" });
        }
        this.isActive = isActive;
        this.loaded.clear();
        return this.update();
    }
    setLevel(level: Level) {
        this.level = level;
        return this.update();
    }
    toCaptureMode(level: Level) {
        for (const image of this.images) {
            image.attr({ display: "block" });
        }
        this.level = level;
        this._isActive = this.isActive;
        this.isActive = true;
        return this.update();
    }
    toDisplayMode(level: Level) {
        this.isActive = this._isActive;
        for (const image of this.images) {
            image.attr({ display: this.isActive ? "block" : "none" });
        }
        this.level = level;
        return this.update();
    }
    async update() {
        if (!this.isActive) {
            return;
        }
        // 過去アーカイブでは容量削減のため Lv3 が削除済み。旧一枚PNGは
        // Lv2を全画面へ拡大すれば、GPV-1.0と同じ表示範囲を維持できる。
        const level = Math.min(this.level, 2) as Level;
        const { ft } = this.forecastTime.getParams();
        await this.loadImages(level, ft);
        this.showImages(level, ft);
        for (const neighbor of this.forecastTime.getNeighborFts(ft)) {
            void this.loadImages(level, neighbor);
        }
    }
    private loadImages(level: number, ft: Decimal) {
        const key = `Lv_${level}_${ft.toFixed(1)}`;
        if (this.loaded.has(key)) {
            const promise = this.loaded.get(key)!;
            return promise;
        }
        const { shadeVariable } = this.shadeSelector.getParams();
        const elms = elementsMap.get(shadeVariable) as Elements;
        const promises: Promise<HTMLImageElement | null>[] = [];
        for (let k = 0; k < 3; k++) {
            const elm = elms[k];
            if (!elm) {
                continue;
            }
            const src = this.imgsrc(elm, level, ft);
            promises.push(loadImage(src));
        }
        const promise = Promise.all(promises);
        this.loaded.set(key, promise);
        return promise;
    }
    private showImages(level: number, ft: Decimal) {
        const { shadeVariable } = this.shadeSelector.getParams();
        const elms = elementsMap.get(shadeVariable) as Elements;
        for (let k = 0; k < 3; k++) {
            const elm = elms[k];
            if (elm) {
                const src = this.imgsrc(elm, level, ft);
                this.images[k].attr({ "xlink:href": src, display: "block" });
            } else {
                this.images[k].attr({ display: "none" });
            }
        }
    }
    private imgsrc(elm: string, level: number, ft: Decimal) {
        const urlbase = dataPath("").replace(/\/$/, "");
        const model = elm.slice(0, 3);
        const { ini } = this.initialTime.getParams();
        if (model !== "QMA") {
            const ann = ini.format("YYYYMMDD-HH");
            const ym = ini.format("YYYYMM");
            const ftstr = `${ft.toFixed(1).padStart(model !== "GS5" && model !== "GSA" ? 4 : 5, "0")}`;
            return `${urlbase}/${elm}/${ym}/${ann}/${elm}-Lv${level}_${ann}_FT${ftstr}.png`;
        } else {
            const ann_ceil = ini
                .add(ft.ceil().toNumber(), "hours")
                .format("YYYYMMDD-HH");
            const ym = ann_ceil.slice(0, 6);
            const ann_floor = ini
                .add(ft.floor().toNumber(), "hours")
                .format("YYYYMMDD-HH");
            const ft_deci = ft.sub(ft.floor()).times(10);
            return `${urlbase}/${elm}/${ym}/${ann_ceil}/${elm}-Lv${level}_${ann_floor}.${ft_deci}.png`;
        }
    }
    updateClipCoast() {
        const clipPath = this.clipCoast.value === "1" ? "url(#clip_coast)" : "";
        this.images[0].attr({ clipPath });
        this.images[1].attr({ clipPath });
        this.images[2].attr({ clipPath });
    }
}

export class ShadeLegend {
    private readonly OV: Snap.Paper;
    private readonly g: Snap.Paper;
    private readonly image: Snap.Paper;

    private x = 1860;
    private y = 1035;
    private scale: number = 1;
    private x0: number = 0;
    private y0: number = 0;

    private readonly orientation = new Selectbox("#select_ILtype");
    private readonly temperature = new Selectbox("#select_ILtemp");
    private temp = -1;
    private defaultTemp = -1;

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
    ) {
        this.orientation.onChange(() => this.update());
        this.temperature.onChange(() => {
            this.temp = Number(this.temperature.value);
            this.update();
        });

        // 気温凡例デフォルト
        const md = Number(dayjs().format("MMDD")); // 暫定・・・。
        if (md > 515 && md <= 915) {
            this.temp = 5; // 40～0
        } else if (md > 915 && md <= 1015) {
            this.temp = 4; // 35～-5
        } else if (md > 1015 && md <= 1115) {
            this.temp = 3; // 30～-10
        } else if (md > 1115 && md <= 1215) {
            this.temp = 2; // 25～-15
        } else if (md > 1215 || md <= 215) {
            this.temp = 1; // 20～-20
        } else if (md > 215 && md <= 315) {
            this.temp = 2; // 25～-15
        } else if (md > 315 && md <= 415) {
            this.temp = 3; // 30～-10
        } else if (md > 415 && md <= 515) {
            this.temp = 4; // 35～-5
        }
        this.defaultTemp = this.temp;

        this.loadParams();
        this.temperature.value = String(this.temp);

        this.OV = Snap("#overlay");
        this.g = this.OV.g()
            .attr({ id: "legend", cursor: "default" })
            .transform(`translate(${this.x},${this.y})`)
            .drag(
                (dx: number, dy: number) => this.dragMove(dx, dy),
                () => this.dragStart(),
                () => this.dragEnd(),
            ) as Snap.Paper;
        this.image = this.g.image("", 0, 0, 0, 0) as Snap.Paper;

        this.update();
    }
    private loadParams() {
        this.x = readNumber(this.params, "lgd_x", 1860, -10000, 10000);
        this.y = readNumber(this.params, "lgd_y", 1035, -10000, 10000);
        this.orientation.value = readChoice(
            this.params,
            "lgd_type",
            ["V", "H", "N"] as const,
            "V",
        );
        this.temp = readNumber(this.params, "lgd_temp", this.defaultTemp, 0, 6);
    }
    async applyParams() {
        this.loadParams();
        this.temperature.value = String(this.temp);
        this.g.transform(`translate(${this.x},${this.y})`);
        await this.update();
    }
    saveParams() {
        this.params.set("lgd_x", this.x.toString());
        this.params.set("lgd_y", this.y.toString());
        this.params.set("lgd_type", this.orientation.value);
        this.params.set("lgd_temp", String(this.temp));
    }
    async update() {
        if (this.orientation.value === "N") {
            this.g.attr({ display: "none" });
            return;
        }
        this.g.attr({ display: "inline" });
        const { shadeVariable } = this.shadeSelector.getParams();
        const legendVariable = shadeVariable.startsWith("GSA")
            ? shadeVariable.replace(/^GSA/, "GS5")
            : shadeVariable === "GSLR"
              ? "GSMNR"
              : shadeVariable === "GSLN"
                ? "GSMN"
                : shadeVariable === "MSGL"
                  ? "MSGL"
                  : shadeVariable;
        let src = "";
        if (this.orientation.value === "N") {
            src = "img/legendN.png";
        } else if (["GSMV", "MSMV"].includes(legendVariable)) {
            src = "img/legend_TSTM.png";
        } else if (legendVariable.slice(-1) === "T") {
            src = `img/legendV_GPVT-${this.temp}.png`;
        } else if (
            legendVariable.slice(-1) !== "N" &&
            legendVariable !== "GSMC"
        ) {
            src = `img/legend${this.orientation.value}_${legendVariable}.png`;
        } else {
            src = "img/legendN.png";
        }
        const image = await loadImage(src);
        if (image) {
            this.image.attr({
                "xlink:href": image.src,
                x: -image.naturalWidth,
                y: -image.naturalHeight,
                width: image.naturalWidth,
                height: image.naturalHeight,
                display: "inline",
            });
        } else {
            this.image.attr({ display: "none" });
        }
    }
    private dragStart() {
        const vb = this.OV.attr("viewBox");
        this.scale = vb.width / Number($("#svg_main").attr("width"));
        const tf = this.g.transform().local;
        this.x0 = Number(tf.split(",")[0].slice(1));
        this.y0 = Number(tf.split(",")[1]);
    }
    private dragMove(dx: number, dy: number) {
        this.x = this.x0 + dx * this.scale;
        this.y = this.y0 + dy * this.scale;
        this.g.transform(`translate(${this.x},${this.y})`);
    }
    private dragEnd() {}
    usePreset(preset: string) {
        switch (preset) {
            case "light":
            case "darkwind":
            case "thunder":
                this.orientation.value = "V";
                this.update();
                break;
            case "trans":
                this.orientation.value = "N";
                this.update();
                break;
        }
    }
}

function loadImage(src: string) {
    return new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
    });
}
